import React from 'react';
import { HStack, Text } from '@chakra-ui/react';
import styles from './NCRs.module.css';
import BubbleChart from '../../../../shared/BubbleChart/BubbleChart';
import { formatDollars, formatSI } from '../../../../modules/format';
import ThisPeriod from './ThisPeriod';
import { RadioGroup, Radio } from '@/components/ui/radio';
import PropTypes from 'prop-types';

const Comparison = props => {
  const { data, singlePackerPlant, packerPlantsInPacker, timeScale, timePeriod } = props;

  const [dataType, setDataType] = React.useState('count');

  const dataSorted = data.sort((a, b) => a[dataType] - b[dataType]);
  const rank =
    singlePackerPlant && !singlePackerPlant.isPacker
      ? dataSorted.findIndex(x => x.packerPlantName === singlePackerPlant.name) + 1
      : undefined;

  const formatDollarsSeparate = value => {
    return (
      <>
        <span className={styles.QualityNcrs_dollar}>$</span>
        {formatDollars(2)(value).replace('$', '')}
      </>
    );
  };

  const multiHighlights = {};

  if (packerPlantsInPacker) {
    packerPlantsInPacker.forEach(x => {
      multiHighlights[x.packerPlantName] = x.color;
    });
  }

  const topList = packerPlantsInPacker && packerPlantsInPacker.filter(x => x.count === 0);

  let noIncidents = false;
  if (topList && packerPlantsInPacker && topList.length === packerPlantsInPacker.length) {
    noIncidents = true;
  }

  const bottomList =
    packerPlantsInPacker && topList && [...packerPlantsInPacker].slice(0, packerPlantsInPacker.length - topList.length);

  while (bottomList && bottomList.length > 5) {
    const pp = bottomList.pop();
    if (topList.length < 5) {
      topList.push(pp);
    }
  }

  return (
    <div className={styles.QualityNcrs_comparison}>
      <div className={styles.QualityNcrs_byCategoryTitle}>
        <Text className={styles.QualityNcrs_label}>
          {packerPlantsInPacker ? `overall rankings` : `comparison against other establishments with incidents`}
        </Text>
        <RadioGroup
          onValueChange={e => {
            setDataType(e.value);
          }}
          defaultValue={dataType}
          size="medium"
          className={styles.QualityNcrs_radio}
        >
          <HStack alignItems="center" justifyContent="end">
            <Radio key={1} value="count">
              Count
            </Radio>
            <Radio key={2} value="claimAmount">
              Claim amount
            </Radio>
          </HStack>
        </RadioGroup>
      </div>
      {!noIncidents && packerPlantsInPacker && packerPlantsInPacker.length > 0 && (
        <div style={{ margin: '10px 0px' }}>
          {topList.length > 0 && (
            <div>
              <span style={{ opacity: 0.6 }}>Top:</span> {topList.map(x => x.packerPlantName).join(', ')}
            </div>
          )}
          {bottomList.length > 0 && (
            <div>
              <span style={{ opacity: 0.6 }}>Bottom:</span>{' '}
              {bottomList
                .slice(0, 5)
                .map(x => x.packerPlantName)
                .join(', ')}
            </div>
          )}
        </div>
      )}
      {rank ? (
        <p className={styles.QualityNcrs_comparisonRank}>
          Rank{' '}
          <strong>
            {rank} out of {dataSorted.length}
          </strong>{' '}
          establishments
        </p>
      ) : (
        <p className={styles.QualityNcrs_comparisonRank}>
          <strong>{dataSorted.length}</strong> establishments
        </p>
      )}
      {!noIncidents && data.length > 0 ? (
        <BubbleChart
          data={data.map(x => ({
            ...x,
            value: x[dataType],
          }))}
          format={dataType === 'count' ? formatSI(1) : formatDollarsSeparate}
          isSmall
          highlights={
            packerPlantsInPacker ? multiHighlights : singlePackerPlant && { [singlePackerPlant.name]: '#2C333D' }
          }
          withEmptyBubbles={!!packerPlantsInPacker}
          getColor={() => '#2C333D'}
          formatTooltip={x =>
            `${x.packerPlantName} - ${dataType === 'count' ? formatSI(1)(x[dataType]) : formatDollars(2)(x[dataType])}`
          }
          nameField="packerPlantName"
          withIntervals
        />
      ) : (
        <p style={{ marginTop: '10px' }}>
          No incidents reported yet in <ThisPeriod timeScale={timeScale} timePeriod={timePeriod} />
        </p>
      )}
    </div>
  );
};

export default Comparison;

Comparison.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      packerPlantName: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      claimAmount: PropTypes.number.isRequired,
    })
  ),
  singlePackerPlant: PropTypes.shape({
    name: PropTypes.string.isRequired,
    isPacker: PropTypes.bool.isRequired,
  }),
  packerPlantsInPacker: PropTypes.arrayOf(
    PropTypes.shape({
      packerPlantName: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      claimAmount: PropTypes.number.isRequired,
      color: PropTypes.string,
    })
  ),
  timeScale: PropTypes.string,
  timePeriod: PropTypes.string,
};
